import {
  getAssessableTypesState,
  getPropertyStatusesState,
} from './../../shared/shared.select'
import {
  ExportListDataRequest,
  FetchAssessableTypesRequest,
  FetchPropertyStatusesRequest,
} from './../../shared/shared.actions'
import { getPropertiesState } from './properties.selectors'
import { Map } from 'immutable'
import { Router, ActivatedRoute } from '@angular/router'
import ListTrait from 'src/app/traits/list.trait'
import { Component, OnInit, OnDestroy } from '@angular/core'
import { Store, select } from '@ngrx/store'
import { Subscription, of } from 'rxjs'
import { fromJS } from 'immutable'
import {
  UpdateField,
  SetDefaultData,
  FetchPropertiesRequest,
} from './properties.actions'
import { TableFilterMatching, TableFilterType } from '@shared/shared.types'
import { filtersToDbQuery } from '@shared/shared.utils'
import { formatNumber } from '@angular/common'

@Component({
  selector: 'app-properties',
  templateUrl: './properties.component.html',
  styleUrls: ['./properties.component.css'],
})
export class PropertiesComponent
  extends ListTrait
  implements OnInit, OnDestroy
{
  private state$: Subscription
  private type$: Subscription
  public type: string
  private shouldReload: boolean = false
  public isLoading: boolean = false
  public statusColor = {
    DATA_ENTRY: '#93c6f5',
    BILLING: '#cae57f',
    ASSESSMENT: '#fad86a',
    OBJECTION: '#fdad67',
    APPROVAL: '#116ec5',
  }
  public columns: any[] = []
  public _columns: any[] = [
    {
      prop: 'propertyCodeAssigned',
      name: 'PROPERTY_CODE',
      template: 'idColumn',
      sortable: true,
      filterable: true,
      filterType: TableFilterType.String,
    },
    {
      prop: 'street',
      name: 'STREET',
      sortable: true,
      filterable: true,
      filterType: TableFilterType.String,
    },
    {
      prop: 'streetNumber',
      name: 'STREET_NUMBER',
      sortable: true,
      filterable: true,
      filterType: TableFilterType.String,
    },
    {
      prop: 'plusCode',
      name: 'PLUS_CODE',
      template: 'plusCode',
      sortable: false,
      filterable: true,
      filterType: TableFilterType.String,
    },
    {
      prop: 'measuredArea',
      name: 'MEASURED_AREA',
      sortable: true,
      filterable: true,
      filterType: TableFilterType.Number,
      render: (_: any, row: Map<string, any>) =>
        formatNumber(row.get('measuredArea'), 'en'),
    },
    {
      prop: 'assessibility',
      name: 'ASSESSIBILITY',
      template: 'assessibility',
      sortable: false,
      filterable: true,
      filterType: TableFilterType.String,
      select$: null, // Will be set in ngOnInit
    },
    {
      prop: 'principalContact',
      name: 'PRINCIPAL_CONTACT',
      sortable: false,
      filterable: true,
      filterType: TableFilterType.String,
    },
    {
      prop: 'appealReason',
      name: 'APPEAL_REASON',
      sortable: false,
      filterable: true,
      filterType: TableFilterType.String,
      template: 'appealReason',
    },
    {
      prop: 'appealNote',
      name: 'APPEAL_NOTE',
      sortable: false,
      filterable: true,
      filterType: TableFilterType.String,
      template: 'appealNote',
    },
    {
      prop: 'status',
      name: 'STATUS',
      template: 'status',
      sortable: false,
      filterable: true,
      filterType: TableFilterType.String,
      select$: null, // Will be set in ngOnInit
    },
    {
      prop: 'fromExternal',
      name: 'FROM_EXTERNAL',
      filterable: true,
      filterType: TableFilterType.Boolean,
      hidden: true,
      select$: of(
        fromJS([
          { name: 'true', title: 'Yes', translationKey: 'YES' },
          { name: 'false', title: 'No', translationKey: 'NO' },
        ]),
      ),
    },
  ]
  public assessableTypes = []
  public filters: any[]

  constructor(
    private store: Store<any>,
    public router: Router,
    private route: ActivatedRoute,
  ) {
    super(router)
  }

  ngOnInit() {
    // Set the select$ properties after store is initialized
    this._columns[5].select$ = this.store.pipe(select(getAssessableTypesState))
    this._columns[9].select$ = this.store.pipe(select(getPropertyStatusesState))

    this.state$ = this.store
      .pipe(select(getPropertiesState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
        this.filters = state.get('filters').toJS()

        if (
          state.get('shouldReload') != this.shouldReload &&
          state.get('shouldReload')
        ) {
          this.handleFilterChange()
        }
        this.shouldReload = state.get('shouldReload')
      })
    this.type$ = this.route.paramMap.subscribe(({ params }: any) => {
      this.store.dispatch<any>(new SetDefaultData('list'))
      this.type = params.type
      this.columns = this._columns.filter(({ name }) => {
        if (this.type !== 'property_register' && name === 'STATUS') {
          return false
        }

        if (this.type === 'billing' && name === 'ASSESSABILITY') {
          return false
        }

        if (this.type !== 'billing' && name === 'PRINCIPAL_CONTACT') {
          return false
        }

        if (
          this.type !== 'assessment' &&
          this.type !== 'objection' &&
          name === 'APPEAL_NOTE'
        ) {
          return false
        }

        if (this.type !== 'objection' && name === 'APPEAL_REASON') {
          return false
        }

        return true
      })

      this.handleFilterChange()
    })

    this.store.dispatch<any>(new FetchAssessableTypesRequest())
    this.store.dispatch<any>(new FetchPropertyStatusesRequest())
  }

  handleDownload() {
    const filters = this.filters

    if (this.type !== 'property_register') {
      filters.push({
        field: 'status',
        condition: 'and',
        matching: TableFilterMatching.Is,
        value: this.type.toUpperCase(),
        type: TableFilterType.String,
      })
    }

    this.store.dispatch(
      new ExportListDataRequest({
        module: 'property',
        filters: filtersToDbQuery(filters),
      }),
    )
  }

  handleFilterChange(data?: any) {
    const filters = data?.filters ?? this.filters
    const pagination = data?.pagination ?? this.state.get('pagination').toJS()
    const { page, page_size, sort, order_by } = pagination

    this.store.dispatch(new UpdateField('filters', filters, 'list'))
    this.store.dispatch(new UpdateField('pagination', pagination, 'list'))

    this.store.dispatch<any>(
      new FetchPropertiesRequest(
        this.type !== 'property_register' ? this.type : null,
        {
          filters: {
            ...filtersToDbQuery(filters),
          },
          pageSize: parseInt(page_size, 10),
          page: parseInt(page, 10) - 1,
          sort: {
            order: sort?.toUpperCase(),
            prop: order_by,
          },
        },
      ),
    )
  }

  handleRefreshChange() {
    //
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.type$.unsubscribe()
  }
}
